import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { SortType } from "../../../../../models/SortType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  parseWalletListQuery,
  walletListQueryToSearchParams,
  WalletApiRow,
  WalletType,
} from "../../../../../models/Swagstore/SwagstoreWalletListing";
import { SwagstoreWalletStore } from "./SwagstoreWalletStore";
import SwagstoreWalletTable from "./SwagstoreWalletTable";
import SwagstoreWalletTransactionsDrawer from "./SwagstoreWalletTransactionsDrawer";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  getModernPageColors,
  modernFilterPanelHeaderSx,
  modernFilterPanelSx,
  modernPageCardSx,
  modernPageShellSx,
} from "../../../../../styles/modernListingPageSx";

const SEARCH_DEBOUNCE_MS = 400;

const WALLET_TYPE_OPTIONS: FilterValue[] = [
  { id: WalletType.CUSTOMER, value: "Customer" },
  { id: WalletType.END_USER, value: "End User" },
];

const SwagstoreWalletListing: React.FC = () => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const { userStore } = useStores();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const canView = userStore.hasPermission(EmployeeRoles.VIEW_SWAGSTORE_WALLET);

  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const storeRef = useRef<SwagstoreWalletStore | null>(null);
  if (!storeRef.current) {
    const initialQuery = parseWalletListQuery(
      Object.fromEntries(new URLSearchParams(location.search)),
    );
    storeRef.current = new SwagstoreWalletStore(initialQuery);
  }
  const store = storeRef.current;

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!canView) return;
    store.fetchWallets().then(() => {
      if (store.error) {
        showError("Error", store.error);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, store.query, canView]);

  useEffect(() => {
    const params = walletListQueryToSearchParams(store.query);
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.query, navigate]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const commitSearch = (value: string) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const trimmed = value.trim();
    if (trimmed === (store.query.search || "")) return;
    store.setQuery({ search: trimmed || undefined, page_no: 1 });
  };

  const handleSearchInput = (value: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(
      () => commitSearch(value),
      SEARCH_DEBOUNCE_MS,
    );
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitSearch((e.target as HTMLInputElement).value);
    }
  };

  const handleSortChange = (sortType: SortType) => {
    store.setQuery({ sort_type: sortType });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    store.setQuery({ page_no: newPage });
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    store.setQuery({ limit: newLimit, page_no: 1 });
  };

  const handleViewTransactions = (row: WalletApiRow) => {
    store.openTransactionsDrawer(row.id, row.customer_name, row.wallet_type);
  };

  const selectedWalletType = WALLET_TYPE_OPTIONS.filter(
    (option) => option.id === store.query.wallet_type,
  );

  const searchPlaceholder =
    store.query.wallet_type === WalletType.END_USER
      ? "Search by customer name or user name or user email"
      : "Search by customer name";

  const handleWalletTypeSelect = (items: FilterValue[]) => {
    store.setQuery({
      wallet_type:
        items.length > 0
          ? (String(items[0].id) as WalletType)
          : WalletType.CUSTOMER,
      page_no: 1,
    });
  };

  return (
    <Box sx={modernPageShellSx(theme)}>
      {canView ? (
        <Paper sx={modernPageCardSx(theme)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
            <CustomTypography variant="heading3" sx={{ color: colors.title }}>
              Swagstore Wallets
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                mt: 0.5,
                color: colors.subtitle,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              View customer Swagstore wallet balances.
            </CustomTypography>
          </Box>

          <Paper variant="outlined" sx={modernFilterPanelSx(theme)}>
            <Box
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              sx={modernFilterPanelHeaderSx(theme, isFiltersExpanded)}
            >
              <CustomTypography variant="heading7" sx={{ color: colors.title }}>
                Search & Filters
              </CustomTypography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFiltersExpanded(!isFiltersExpanded);
                  }}
                >
                  {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
                <Grid2 container spacing={2} columns={12} padding={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 9 }}>
                    <CustomTextField
                      width={"100%"}
                      placeholder={searchPlaceholder}
                      value={store.query.search ?? ""}
                      onSearch={handleSearchInput}
                      onKeyDown={handleSearchKeyDown}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Wallet Type"
                      isMultiSelect={false}
                      value={selectedWalletType}
                      list={WALLET_TYPE_OPTIONS}
                      local={true}
                      minWidth="100%"
                      handleSelect={handleWalletTypeSelect}
                    />
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>

            <SwagstoreWalletTable
              rows={store.wallets}
              loading={store.loading}
              pageNo={store.query.page_no}
              limit={store.query.limit}
              totalCount={store.totalCount}
              sortType={store.query.sort_type}
              onSortChange={handleSortChange}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onViewTransactions={handleViewTransactions}
            />
        </Paper>
      ) : (
        <PermissionBlockLabel />
      )}
      <SwagstoreWalletTransactionsDrawer store={store} />
    </Box>
  );
};

export default observer(SwagstoreWalletListing);
